<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { templatesInSection } from 'dashboard/helper/templateSections';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import ContentTemplateForm from './ContentTemplateForm.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['sendMessage']);

const { t } = useI18n();
const store = useStore();
const inbox = useMapGetter('inboxes/getInbox');
const currentUser = useMapGetter('getCurrentUser');
const { uiSettings, updateUISettings } = useUISettings();

const searchQuery = ref('');
const selectedTemplate = ref(null);
const activeTab = ref('favoritos');

const allTemplates = computed(() => {
  const inboxData = inbox.value(props.inboxId);
  return (inboxData?.content_templates?.templates || []).filter(
    tpl => tpl.status === 'approved'
  );
});

// ============================================================
// Secciones personalizadas DE LA BANDEJA (Ajustes > Plantillas): mismas
// pestañas "Favoritos" + secciones que usa el panel de plantillas del
// reply box (ContentTemplatesPanel.vue) dentro de una conversación.
// ============================================================
const sections = computed(() =>
  props.inboxId
    ? store.getters['templateClassifications/getSections'](props.inboxId)
    : []
);
const hydratedTemplates = computed(() =>
  props.inboxId
    ? store.getters['templateClassifications/getTemplates'](props.inboxId)
    : []
);

const TABS = computed(() => [
  { key: 'favoritos', label: t('CONTENT_TEMPLATES.PANEL.FAVORITES_TAB') },
  ...sections.value.map(s => ({ key: `sec_${s.id}`, id: s.id, label: s.name })),
]);

const activeTabObject = computed(() =>
  TABS.value.find(tab => tab.key === activeTab.value)
);

const fetchInboxSections = () => {
  if (!props.inboxId) return;
  const loaded = store.getters['templateClassifications/isInboxLoaded'](
    props.inboxId
  );
  if (!loaded) {
    // eslint-disable-next-line no-console
    console.log(
      '[ContentTemplateSelector] fetching sections for inbox',
      props.inboxId
    );
    store.dispatch('templateClassifications/get', { inboxId: props.inboxId });
  }
};
onMounted(fetchInboxSections);
watch(() => props.inboxId, fetchInboxSections);

// Si desaparece la sección activa (p. ej. cambió la bandeja), volver a Favoritos.
watch(TABS, tabs => {
  if (!tabs.some(tab => tab.key === activeTab.value)) {
    activeTab.value = 'favoritos';
  }
});

// Nombres ordenados de una sección, según el drag & drop del admin (una
// plantilla puede aparecer en varias secciones).
const namesForSection = sectionId =>
  templatesInSection(hydratedTemplates.value, sectionId).map(tpl => tpl.name);

// Favoritos guardados en ui_settings como array de content_sid (mismo campo
// que usa el panel del reply box, así que coinciden en ambos sitios).
const favoriteIds = computed(() => uiSettings.value?.template_favorites || []);
const isFavorite = tpl => favoriteIds.value.includes(tpl.content_sid);
const toggleFavorite = tpl => {
  const current = favoriteIds.value;
  const next = isFavorite(tpl)
    ? current.filter(id => id !== tpl.content_sid)
    : [...current, tpl.content_sid];
  updateUISettings({ template_favorites: next });
};

// Alias de visualización por bandeja (definidos en Ajustes > Plantillas):
// la plantilla se muestra Y SE BUSCA también por su nombre personalizado.
const templateDisplayNames = computed(() => {
  const fromInbox = {};
  hydratedTemplates.value.forEach(tpl => {
    if (tpl.display_name) fromInbox[tpl.name] = tpl.display_name;
  });
  return Object.keys(fromInbox).length
    ? fromInbox
    : currentUser.value?.template_display_names || {};
});
const displayNameOf = template =>
  templateDisplayNames.value[template.friendly_name] || template.friendly_name;

// Orden manual (drag & drop del admin en Ajustes > Plantillas) dentro de una sección.
const orderByList = (templates, names) => {
  const idx = new Map(names.map((n, i) => [n, i]));
  return [...templates].sort(
    (a, b) =>
      (idx.has(a.friendly_name) ? idx.get(a.friendly_name) : Infinity) -
      (idx.has(b.friendly_name) ? idx.get(b.friendly_name) : Infinity)
  );
};

const templatesForTab = computed(() => {
  const q = searchQuery.value.toLowerCase();
  let list = [];

  if (activeTab.value === 'favoritos') {
    list = allTemplates.value.filter(tpl => isFavorite(tpl));
  } else if (activeTabObject.value?.id) {
    const names = namesForSection(activeTabObject.value.id);
    const set = new Set(names);
    list = set.size
      ? orderByList(
          allTemplates.value.filter(tpl => set.has(tpl.friendly_name)),
          names
        )
      : [];
  }

  if (!q) return list;
  return list.filter(
    tpl =>
      tpl.friendly_name.toLowerCase().includes(q) ||
      displayNameOf(tpl).toLowerCase().includes(q)
  );
});

// Sección sin plantillas asignadas todavía (se ordenan en Ajustes > Plantillas).
const isEmptySectionTab = computed(() => {
  const tab = activeTabObject.value;
  if (!tab?.id) return false;
  return namesForSection(tab.id).length === 0;
});

const selectTab = tab => {
  activeTab.value = tab;
  searchQuery.value = '';
};

const handlePopoverShow = () => {
  searchQuery.value = '';
  selectedTemplate.value = null;
  activeTab.value = 'favoritos';
};

const handlePopoverHide = () => {
  selectedTemplate.value = null;
};

const handleTemplateClick = template => {
  selectedTemplate.value = template;
};

const handleBack = () => {
  selectedTemplate.value = null;
};

const handleSendMessage = (template, hide) => {
  emit('sendMessage', template);
  hide();
};
</script>

<template>
  <Popover
    align="start"
    disable-mobile-view
    @show="handlePopoverShow"
    @hide="handlePopoverHide"
  >
    <Button
      icon="i-ph-whatsapp-logo"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.TWILIO_OPTIONS.LABEL')"
      color="slate"
      size="sm"
      :disabled="selectedTemplate"
      class="!text-xs font-medium"
    />
    <template #content="{ hide }">
      <div v-if="!selectedTemplate" class="flex flex-col w-96 max-h-[28rem]">
        <!-- Tabs: Favoritos + secciones de la bandeja -->
        <div
          class="flex border-b border-n-weak px-2 pt-1 gap-0.5 flex-shrink-0 overflow-x-auto"
        >
          <button
            v-for="tab in TABS"
            :key="tab.key"
            type="button"
            class="px-3 py-2 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap"
            :class="
              activeTab === tab.key
                ? 'text-n-brand border-b-2 border-n-brand bg-n-alpha-1'
                : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="selectTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Search -->
        <div class="px-3 pt-3 pb-2 flex-shrink-0">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.TWILIO_OPTIONS.SEARCH_PLACEHOLDER'
              )
            "
            custom-input-class="ltr:pl-10 rtl:pr-10"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute top-2 size-3.5 ltr:left-3 rtl:right-3"
              />
            </template>
          </Input>
        </div>

        <!-- Empty favorites notice -->
        <div
          v-if="activeTab === 'favoritos' && templatesForTab.length === 0"
          class="flex flex-col items-center justify-center px-4 py-8 text-center gap-2"
        >
          <Icon icon="i-ph-star" class="size-7 text-n-slate-9" />
          <p class="text-sm text-n-slate-11">
            {{ t('CONTENT_TEMPLATES.PANEL.EMPTY_FAVORITES_TITLE') }}
          </p>
          <p class="text-xs text-n-slate-9">
            {{ t('CONTENT_TEMPLATES.PANEL.EMPTY_FAVORITES_HINT') }}
          </p>
        </div>

        <!-- Empty section notice -->
        <div
          v-else-if="isEmptySectionTab"
          class="flex flex-col items-center justify-center px-4 py-8 text-center gap-2"
        >
          <Icon icon="i-ph-list-dashes" class="size-7 text-n-slate-9" />
          <p class="text-sm text-n-slate-11">
            {{ t('CONTENT_TEMPLATES.PANEL.EMPTY_SECTION_TITLE') }}
          </p>
          <p class="text-xs text-n-slate-9">
            {{ t('CONTENT_TEMPLATES.PANEL.EMPTY_SECTION_HINT') }}
          </p>
        </div>

        <!-- Template list -->
        <div v-else class="px-2 pb-3">
          <div
            v-for="template in templatesForTab"
            :key="template.content_sid"
            class="flex items-start gap-2 px-2 py-2.5 rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 transition-colors"
          >
            <button
              type="button"
              class="flex-shrink-0 mt-0.5 transition-colors"
              :class="
                isFavorite(template)
                  ? 'text-y-600 hover:text-y-700 dark:text-y-400 dark:hover:text-y-300'
                  : 'text-n-slate-10 hover:text-y-600 dark:hover:text-y-400'
              "
              :title="
                isFavorite(template)
                  ? t('CONTENT_TEMPLATES.PANEL.REMOVE_FAVORITE')
                  : t('CONTENT_TEMPLATES.PANEL.ADD_FAVORITE')
              "
              @click.stop="toggleFavorite(template)"
            >
              <Icon
                :icon="isFavorite(template) ? 'i-ph-star-fill' : 'i-ph-star'"
                class="size-4"
              />
            </button>
            <button
              type="button"
              class="flex-1 text-left min-w-0"
              @click="handleTemplateClick(template)"
            >
              <span class="text-sm text-n-slate-12">
                {{ displayNameOf(template) }}
                <span
                  v-if="displayNameOf(template) !== template.friendly_name"
                  class="ms-1 text-[10px] text-n-slate-9"
                >
                  ({{ template.friendly_name }})
                </span>
              </span>
              <p class="mb-0 text-xs leading-5 text-n-slate-11 line-clamp-2">
                {{ template.body || t('CONTENT_TEMPLATES.PICKER.NO_CONTENT') }}
              </p>
            </button>
          </div>
          <p
            v-if="templatesForTab.length === 0"
            class="pt-6 pb-2 w-full text-sm text-n-slate-11 text-center"
          >
            {{
              searchQuery
                ? t('CONTENT_TEMPLATES.PICKER.NO_TEMPLATES_FOUND')
                : t('COMPOSE_NEW_CONVERSATION.FORM.TWILIO_OPTIONS.EMPTY_STATE')
            }}
          </p>
        </div>
      </div>
      <ContentTemplateForm
        v-else
        :template="selectedTemplate"
        @send-message="payload => handleSendMessage(payload, hide)"
        @back="handleBack"
      />
    </template>
  </Popover>
</template>
